/**
 * Detects whether the app is being rendered from an installed PWA
 * (standalone display mode) rather than a normal browser tab.
 *
 * We combine the two supported detection APIs:
 *   • matchMedia('(display-mode: standalone)') — Android, Chromium desktop,
 *     Safari 16.4+.
 *   • navigator.standalone === true — legacy iOS Safari.
 *
 * The value is computed once at mount and then kept in sync via a `change`
 * listener so users who install the PWA mid-session (rare) still get updates.
 */
import { useEffect, useState } from "react";

function detectStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  } catch { /* matchMedia unsupported — fall through to navigator.standalone */ }
  if (typeof window.navigator?.standalone === "boolean" && window.navigator.standalone) return true;
  return false;
}

export function usePwaMode() {
  const [isPwa, setIsPwa] = useState(() => detectStandalone());

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mm = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setIsPwa(detectStandalone());
    // Some Safari versions still expose the old API.
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else if (mm.addListener) mm.addListener(onChange);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else if (mm.removeListener) mm.removeListener(onChange);
    };
  }, []);

  return isPwa;
}
